import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Shield, Zap, CheckCircle2, Clock, Circle, MinusCircle,
  Save, Loader2, Search, X, Library, Paperclip, Trash2, Percent, ShieldCheck,
} from 'lucide-react';

const STATUSES = ['Not Assessed', 'Non Compliant', 'Partially Compliant', 'Compliant', 'Not Applicable'];

const STATUS_META = {
  'Not Assessed':          { color: 'bg-slate-100 text-slate-700 border-slate-200',       icon: Circle,       dot: 'bg-slate-400' },
  'Non Compliant':         { color: 'bg-red-100 text-red-800 border-red-200',             icon: MinusCircle,  dot: 'bg-red-500' },
  'Partially Compliant':   { color: 'bg-blue-100 text-blue-800 border-blue-200',          icon: Clock,        dot: 'bg-blue-500' },
  'Compliant':             { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2, dot: 'bg-emerald-500' },
  'Not Applicable':        { color: 'bg-slate-50 text-slate-500 border-slate-200',        icon: MinusCircle,  dot: 'bg-slate-300' },
};

const PRIORITY_COLORS = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
};

// framework routes -> framework_id
const ROUTE_TO_FRAMEWORK = {
  iso27001: 'ISO27001:2022',
  soc2: 'SOC2',
  essential8: 'EssentialEight',
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META['Not Assessed'];
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.color} border text-xs inline-flex items-center gap-1`}>
      <Icon className="w-3 h-3" /> {status}
    </Badge>
  );
}

function SummaryCard({ label, value, tone = 'slate', testid }) {
  const toneMap = {
    slate: 'text-slate-900',
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    muted: 'text-slate-400',
  };
  return (
    <Card className="border-slate-200">
      <CardContent className="py-4 px-5">
        <div className={`text-2xl font-bold ${toneMap[tone]}`} data-testid={testid}>{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </CardContent>
    </Card>
  );
}

function AssessmentDialog({ open, onOpenChange, mc, tenantParam, canWrite = true, onSaved }) {
  const [status, setStatus] = useState('Not Assessed');
  const [applicability, setApplicability] = useState('Applicable');
  const [applicabilityJustification, setApplicabilityJustification] = useState('');
  const [implementationDetails, setImplementationDetails] = useState('');
  const [notes, setNotes] = useState('');
  const [assessorComments, setAssessorComments] = useState('');
  const [owner, setOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mc?.assessment) {
      setStatus(mc.assessment.status || 'Not Assessed');
      setApplicability(mc.assessment.applicability || 'Applicable');
      setApplicabilityJustification(mc.assessment.applicability_justification || '');
      setImplementationDetails(mc.assessment.implementation_details || '');
      setNotes(mc.assessment.notes || '');
      setAssessorComments(mc.assessment.assessor_comments || '');
      setOwner(mc.assessment.owner || '');
      setDueDate(mc.assessment.due_date || '');
      setEvidenceFiles(mc.assessment.evidence_files || []);
    }
  }, [mc]);

  if (!mc) return null;

  const isNA = applicability === 'Not Applicable';

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status: isNA ? 'Not Applicable' : status,
        applicability,
        applicability_justification: applicabilityJustification || null,
        implementation_details: implementationDetails || null,
        notes: notes || null,
        assessor_comments: assessorComments || null,
        owner: owner || null,
        due_date: dueDate || null,
      };
      const res = await axios.put(
        `${API}/master-controls/${mc.control_id}/assessment${tenantParam}`,
        payload,
      );
      toast.success(`Saved ${mc.control_id}. Shared across every mapped framework.`);
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (fileInput) => {
    const file = fileInput.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const url = `${API}/master-controls/${mc.control_id}/evidence${tenantParam}`;
      const res = await axios.post(url, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEvidenceFiles((prev) => [...prev, res.data.evidence]);
      toast.success('Evidence uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      fileInput.value = '';
    }
  };

  const removeEvidence = async (documentId) => {
    if (!window.confirm('Delete this evidence file?')) return;
    try {
      await axios.delete(`${API}/master-controls/${mc.control_id}/evidence/${documentId}${tenantParam}`);
      setEvidenceFiles((prev) => prev.filter((f) => f.document_id !== documentId));
      toast.success('Evidence removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const mappedFrameworks = (mc.mappings || []).map((m) => `${m.framework_id} (${(m.codes || []).join(', ')})`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assessment-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-slate-500 text-base">{mc.control_id}</span>
            <span>{mc.name}</span>
          </DialogTitle>
          <DialogDescription>{mc.domain}</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-slate-600 mt-2">{mc.description}</p>

        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          <strong>Assess once → map many.</strong> This assessment is shared across every
          framework requirement this control satisfies:{' '}
          {mappedFrameworks.length ? mappedFrameworks.join(' · ') : 'no framework mappings yet'}.
        </div>

        {!canWrite && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            You have read-only access to assessments. Contact an admin to update status,
            gap description, or recommendations.
          </div>
        )}
        <fieldset disabled={!canWrite} className={!canWrite ? 'opacity-90' : ''} data-testid="assessment-fieldset">

        {/* Applicability */}
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Applicability</label>
            <Select value={applicability} onValueChange={setApplicability}>
              <SelectTrigger data-testid="assessment-applicability-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Applicable">Applicable</SelectItem>
                <SelectItem value="Not Applicable">Not Applicable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Implementation status</label>
            <Select value={isNA ? 'Not Applicable' : status} onValueChange={setStatus} disabled={isNA}>
              <SelectTrigger
                data-testid="assessment-status-select"
                className="mt-1"
                disabled={isNA}
                aria-disabled={isNA}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.filter((s) => s !== 'Not Applicable').map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isNA && (
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Not-applicable justification
            </label>
            <Textarea
              data-testid="assessment-na-justification-input"
              value={applicabilityJustification}
              onChange={(e) => setApplicabilityJustification(e.target.value)}
              rows={2}
              placeholder="Why is this control not applicable to this tenant/scope?"
              className="mt-1"
            />
          </div>
        )}

        <div className="mt-3 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Owner</label>
            <Input
              data-testid="assessment-owner-input"
              value={owner}
              placeholder={mc.owner || 'e.g. CISO / Head of Security'}
              onChange={(e) => setOwner(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Due date</label>
            <Input
              data-testid="assessment-due-date-input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Implementation details
          </label>
          <Textarea
            data-testid="assessment-implementation-input"
            value={implementationDetails}
            placeholder="How is this control technically implemented? Systems, procedures, policies…"
            rows={3}
            onChange={(e) => setImplementationDetails(e.target.value)}
            className="mt-1"
          />
        </div>

        <div className="mt-3 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Notes</label>
            <Textarea
              data-testid="assessment-notes-input"
              value={notes}
              placeholder="Free-form notes, references, dates…"
              rows={3}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Assessor comments</label>
            <Textarea
              data-testid="assessment-assessor-comments-input"
              value={assessorComments}
              placeholder="Client findings, gaps, corrective actions…"
              rows={3}
              onChange={(e) => setAssessorComments(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        {/* Evidence */}
        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
            <Paperclip className="w-3.5 h-3.5" /> Evidence files
            <span className="text-slate-400 font-normal normal-case">({evidenceFiles.length})</span>
          </label>
          <div className="mt-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
            {evidenceFiles.length > 0 && (
              <ul className="mb-3 space-y-1">
                {evidenceFiles.map((f) => (
                  <li
                    key={f.document_id}
                    className="flex items-center justify-between bg-white border border-slate-200 rounded px-2 py-1 text-xs"
                    data-testid={`evidence-item-${f.document_id}`}
                  >
                    <div className="min-w-0 flex-1 truncate">
                      <span className="font-medium text-slate-800">{f.file_name}</span>
                      <span className="text-slate-500 ml-2">
                        {f.document_type} · {(f.file_size / 1024).toFixed(1)} KB
                      </span>
                      {f.description && <div className="text-slate-500 truncate">{f.description}</div>}
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => removeEvidence(f.document_id)}
                      data-testid={`evidence-remove-${f.document_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <label className="inline-flex items-center gap-2 text-xs text-blue-700 cursor-pointer">
              <input
                type="file"
                onChange={(e) => handleUpload(e.target)}
                disabled={uploading}
                className="hidden"
                data-testid="evidence-upload-input"
              />
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-blue-200 rounded hover:bg-blue-50">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading…' : 'Attach evidence file (max 25 MB)'}
              </span>
            </label>
            {mc.evidence_required && (
              <p className="text-[11px] text-slate-500 mt-2">
                <strong>Evidence required:</strong> {mc.evidence_required}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1 text-xs text-slate-700">
          {mc.recommendation && <div><strong>Recommendation:</strong> {mc.recommendation}</div>}
          {mc.m365_guidance && <div><strong>M365:</strong> {mc.m365_guidance}</div>}
          {mc.azure_guidance && <div><strong>Azure:</strong> {mc.azure_guidance}</div>}
        </div>

        </fieldset>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="assessment-cancel-btn">Cancel</Button>
          {canWrite && (
            <Button onClick={handleSave} disabled={saving} data-testid="assessment-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save assessment
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ControlRow({ mc, onOpen }) {
  const meta = STATUS_META[mc.assessment?.status || 'Not Assessed'];
  const evidenceCount = (mc.assessment?.evidence_files || []).length;
  return (
    <tr
      onClick={() => onOpen(mc)}
      className="cursor-pointer hover:bg-slate-50 border-b border-slate-100"
      data-testid={`fw-row-${mc.control_id}`}
    >
      <td className="pl-10 pr-3 py-2 w-[110px]">
        <span className="font-mono text-slate-600 text-sm">{mc.control_id}</span>
      </td>
      <td className="py-2 pr-3">
        <div className="text-sm font-medium text-slate-900">{mc.name}</div>
        <div className="text-xs text-slate-500 line-clamp-1">{mc.description}</div>
      </td>
      <td className="py-2 pr-3 text-sm text-slate-700">{mc.domain}</td>
      <td className="py-2 pr-3">
        {mc.priority && (
          <Badge className={`${PRIORITY_COLORS[mc.priority] || ''} border text-xs`}>{mc.priority}</Badge>
        )}
      </td>
      <td className="py-2 pr-3 text-center">
        {evidenceCount > 0 && (
          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-xs">
            <Paperclip className="w-3 h-3 mr-1" />{evidenceCount}
          </Badge>
        )}
      </td>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${meta.dot}`}></span>
          <StatusPill status={mc.assessment?.status || 'Not Assessed'} />
        </div>
      </td>
    </tr>
  );
}

function GroupBlock({ group, expanded, onToggle, onOpen }) {
  const total = group.master_controls.length;
  const compliance = group.compliance || {};
  const pct = compliance.percentage;
  const pctColor = pct == null
    ? 'bg-slate-100 text-slate-600'
    : pct >= 80 ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : pct >= 50 ? 'bg-blue-100 text-blue-800 border-blue-200'
    : pct >= 20 ? 'bg-amber-100 text-amber-800 border-amber-200'
    : 'bg-red-100 text-red-800 border-red-200';
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`fw-group-${group.code}`}>
      <button
        onClick={onToggle}
        data-testid={`fw-group-toggle-${group.code}`}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <span className="font-mono font-semibold text-slate-800">{group.code}</span>
          <span className="text-sm text-slate-500">· {total} control{total > 1 ? 's' : ''}</span>
          {compliance.status && compliance.status !== 'Not Assessed' && (
            <StatusPill status={compliance.status} />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge
            className={`${pctColor} border font-semibold`}
            data-testid={`fw-group-compliance-${group.code}`}
          >
            <Percent className="w-3 h-3 mr-1" />
            {pct == null ? 'N/A' : `${pct}%`}
          </Badge>
        </div>
      </button>
      {expanded && (
        <table className="w-full">
          <tbody>
            {group.master_controls.map((mc) => (
              <ControlRow key={`${group.code}::${mc.control_id}`} mc={mc} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function FrameworkViewPage() {
  const location = useLocation();
  const { user, viewingOrg } = useContext(AuthContext);
  const canWrite = user?.role === 'admin' || user?.role === 'super_admin';
  // Derive route key from pathname (/iso27001 -> 'iso27001').
  const routeKey = location.pathname.replace(/^\//, '').split('/')[0];
  const frameworkId = ROUTE_TO_FRAMEWORK[routeKey];

  const tenantParam = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogControl, setDialogControl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!frameworkId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/frameworks-v2/${encodeURIComponent(frameworkId)}/controls${tenantParam}`);
      setData(res.data);
      // Expand all groups by default only if we haven't set state yet.
      setExpanded((prev) => {
        if (Object.keys(prev).length) return prev;
        const initExpanded = {};
        (res.data.groups || []).forEach((g) => { initExpanded[g.code] = true; });
        return initExpanded;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load framework view');
    } finally {
      setLoading(false);
    }
  }, [frameworkId, tenantParam]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => {
    // Refetch so the backend computes updated per-group + framework compliance,
    // and cross-framework sync is reflected instantly.
    load();
  };

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.groups
      .map((g) => ({
        ...g,
        master_controls: g.master_controls.filter((c) => {
          if (statusFilter !== 'all' && (c.assessment?.status || 'Not Assessed') !== statusFilter) return false;
          if (!s) return true;
          return (
            c.control_id.toLowerCase().includes(s) ||
            (c.name || '').toLowerCase().includes(s) ||
            (c.description || '').toLowerCase().includes(s) ||
            (c.domain || '').toLowerCase().includes(s) ||
            g.code.toLowerCase().includes(s)
          );
        }),
      }))
      .filter((g) => g.master_controls.length > 0);
  }, [data, search, statusFilter]);

  if (!frameworkId) {
    return <div className="p-8 text-slate-600">Unknown framework: {routeKey}</div>;
  }

  const fw = data?.framework;
  const sum = data?.summary;

  return (
    <div className="space-y-6" data-testid="framework-view-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" />
            <span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span>
            <span>Framework View</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Shield className="w-7 h-7" style={{ color: fw?.color || '#0f172a' }} />
            {fw?.name || frameworkId}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Backed by the Master Control Library — updating any control&apos;s status here
            is reflected across every framework that shares it.
          </p>
          <div className="mt-2">
            <Link
              to={`/frameworks/${routeKey}/requirements`}
              className="text-xs font-medium text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
              data-testid="fw-goto-requirements-link"
            >
              <Library className="w-3.5 h-3.5" />
              Browse {fw?.short_name || frameworkId} requirements &amp; mappings →
            </Link>
          </div>
        </div>
      </div>

      {/* Mandatory / Recommended per-type cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fw-type-metrics">
        <Card className="border-slate-900/20">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-900 mb-2">Mandatory</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="fw-mandatory-total">{sum?.mandatory?.total ?? 0}</div>
            <div className="text-xs text-slate-500">total controls · {sum?.mandatory?.percentage ?? 0}% complete</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-500">Compliant</span> <span className="font-semibold text-emerald-700 ml-1" data-testid="fw-mandatory-implemented">{sum?.mandatory?.counts?.Compliant ?? 0}</span></div>
              <div><span className="text-slate-500">Partially Compliant</span> <span className="font-semibold text-blue-700 ml-1">{sum?.mandatory?.counts?.['Partially Compliant'] ?? 0}</span></div>
              <div><span className="text-slate-500">Non Compliant</span> <span className="font-semibold text-red-700 ml-1">{sum?.mandatory?.counts?.['Non Compliant'] ?? 0}</span></div>
              <div><span className="text-slate-500">Not Applicable</span> <span className="font-semibold text-slate-500 ml-1" data-testid="fw-mandatory-not-applicable">{sum?.mandatory?.counts?.['Not Applicable'] ?? 0}</span></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide font-semibold text-indigo-800 mb-2">Recommended</div>
            <div className="text-2xl font-bold text-indigo-900" data-testid="fw-recommended-total">{sum?.recommended?.total ?? 0}</div>
            <div className="text-xs text-indigo-700">tracked separately · {sum?.recommended?.percentage ?? 0}% complete</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-indigo-700/70">Compliant</span> <span className="font-semibold text-emerald-700 ml-1" data-testid="fw-recommended-implemented">{sum?.recommended?.counts?.Compliant ?? 0}</span></div>
              <div><span className="text-indigo-700/70">Partially Compliant</span> <span className="font-semibold text-blue-700 ml-1">{sum?.recommended?.counts?.['Partially Compliant'] ?? 0}</span></div>
              <div><span className="text-indigo-700/70">Non Compliant</span> <span className="font-semibold text-red-700 ml-1">{sum?.recommended?.counts?.['Non Compliant'] ?? 0}</span></div>
              <div><span className="text-indigo-700/70">Not Applicable</span> <span className="font-semibold text-indigo-900/60 ml-1" data-testid="fw-recommended-not-applicable">{sum?.recommended?.counts?.['Not Applicable'] ?? 0}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <SummaryCard label="Total controls" value={sum?.total ?? 0} testid="fw-summary-total" />
        <SummaryCard label="Compliant" value={sum?.Compliant ?? 0} tone="emerald" testid="fw-summary-implemented" />
        <SummaryCard label="Partially Compliant" value={sum?.['Partially Compliant'] ?? 0} testid="fw-summary-partial" />
        <SummaryCard label="Non Compliant" value={sum?.['Non Compliant'] ?? 0} tone="red" testid="fw-summary-non-compliant" />
        <SummaryCard label="Not Applicable" value={sum?.['Not Applicable'] ?? 0} testid="fw-summary-na" />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="fw-search-input"
              value={search}
              placeholder="Search by requirement code, control ID, name…"
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="fw-status-filter" className="w-[220px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {(search || statusFilter !== 'all') && (
            <Button
              variant="ghost"
              onClick={() => { setSearch(''); setStatusFilter('all'); }}
              data-testid="fw-clear-filters"
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 inline animate-spin mr-2" /> Loading framework view…
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No controls match the current filters.</div>
      ) : (
        <TooltipProvider>
          <div className="space-y-3">
            {filteredGroups.map((g) => (
              <GroupBlock
                key={g.code}
                group={g}
                expanded={!!expanded[g.code]}
                onToggle={() => setExpanded((prev) => ({ ...prev, [g.code]: !prev[g.code] }))}
                onOpen={(mc) => { setDialogControl(mc); setDialogOpen(true); }}
              />
            ))}
          </div>
        </TooltipProvider>
      )}

      <AssessmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mc={dialogControl}
        tenantParam={tenantParam}
        canWrite={canWrite}
        onSaved={handleSaved}
      />
    </div>
  );
}
